import type { Movimiento, Moneda, DistribucionUtilidad } from '@/contabilidad/types';
import type { DateRange } from '@/contabilidad/hooks/useDateRangeFilter';
import { parseFecha } from '@/contabilidad/utils/dateRange';

export interface DistribucionSlice {
  name: string;
  value: number;
  porcentaje: number;
}

/** Paleta de tonos de azul para diferenciar socios en los gráficos de utilidad (mismo estilo que INGRESOS_PALETTE). */
export const SOCIOS_PALETTE = ['#2C5282', '#3182CE', '#4299E1', '#63B3ED', '#90CDF4', '#BEE3F8'];

/**
 * Asigna un color estable por socio (según su índice en la lista dada, sin
 * importar el orden en que luego se muestren los datos). Permite que el mismo
 * socio use el mismo color tanto en el donut de participación como en las
 * barras de evolución mensual, aunque cada gráfico ordene sus datos distinto.
 */
export function buildSocioColorMap(socios: string[]): Record<string, string> {
  return Object.fromEntries(socios.map((s, i) => [s, SOCIOS_PALETTE[i % SOCIOS_PALETTE.length]]));
}

const MESES_ABREV = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Agrupa el historial de distribuciones por socio, sumando lo retirado en la
 * moneda indicada. Excluye distribuciones canceladas. Devuelve el mismo shape
 * que `aggregateByCategoria` para poder reutilizar `DistribucionChart`.
 */
export function aggregateBySocio(
  distribuciones: DistribucionUtilidad[],
  moneda: Moneda,
): DistribucionSlice[] {
  const totals = new Map<string, number>();
  for (const d of distribuciones) {
    if (d.estado === 'Cancelado') continue;
    for (const detalle of d.detalles ?? []) {
      const monto = moneda === 'ARS' ? detalle.montoARS : detalle.montoUSD;
      if (!monto) continue;
      totals.set(detalle.socioNombre, (totals.get(detalle.socioNombre) ?? 0) + monto);
    }
  }

  const total = Array.from(totals.values()).reduce((acc, v) => acc + v, 0);
  if (total === 0) return [];

  return Array.from(totals.entries())
    .map(([name, value]) => ({ name, value, porcentaje: Math.round((value / total) * 100) }))
    .sort((a, b) => b.value - a.value);
}

export interface RepartoMensualPunto {
  mes: string;
  [socioNombre: string]: number | string;
}

/**
 * Arma la serie mensual de repartos por socio dentro del rango dado, rellenando
 * los meses sin datos con 0 (mismo criterio que `buildChartData` del backend).
 * Excluye distribuciones canceladas.
 */
export function buildRepartosMensuales(
  distribuciones: DistribucionUtilidad[],
  moneda: Moneda,
  range: DateRange | null,
): { data: RepartoMensualPunto[]; socios: string[] } {
  const vigentes = distribuciones.filter((d) => d.estado !== 'Cancelado');

  const socios = Array.from(
    new Set(vigentes.flatMap((d) => (d.detalles ?? []).map((det) => det.socioNombre))),
  ).sort();

  if (!range) {
    return { data: [], socios };
  }

  // Totales por año-mes y socio.
  const totalsByMonth = new Map<string, Map<string, number>>();
  for (const d of vigentes) {
    const fecha = parseFecha(d.fechaReparto);
    if (!fecha) continue;
    const key = `${fecha.getFullYear()}-${fecha.getMonth()}`;
    const bucket = totalsByMonth.get(key) ?? new Map<string, number>();
    for (const detalle of d.detalles ?? []) {
      const monto = moneda === 'ARS' ? detalle.montoARS : detalle.montoUSD;
      bucket.set(detalle.socioNombre, (bucket.get(detalle.socioNombre) ?? 0) + monto);
    }
    totalsByMonth.set(key, bucket);
  }

  const data: RepartoMensualPunto[] = [];
  const cursor = new Date(range.start.getFullYear(), range.start.getMonth(), 1);
  const fin = new Date(range.end.getFullYear(), range.end.getMonth(), 1);
  const incluirAnio = range.start.getFullYear() !== range.end.getFullYear();

  while (cursor <= fin) {
    const key = `${cursor.getFullYear()}-${cursor.getMonth()}`;
    const bucket = totalsByMonth.get(key);
    const label = incluirAnio
      ? `${MESES_ABREV[cursor.getMonth()]} '${String(cursor.getFullYear()).slice(-2)}`
      : MESES_ABREV[cursor.getMonth()];

    const punto: RepartoMensualPunto = { mes: label };
    for (const socio of socios) {
      punto[socio] = bucket?.get(socio) ?? 0;
    }
    data.push(punto);

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return { data, socios };
}

/**
 * Agrupa los movimientos por categoría, filtrando por tipo y moneda.
 * Devuelve los top 5 slices + "Otros" si hay más, ordenados por valor desc.
 */
export function aggregateByCategoria(
  movimientos: Movimiento[],
  tipo: 'Ingreso' | 'Egreso',
  moneda: Moneda,
  topN = 5,
): DistribucionSlice[] {
  const filtered = movimientos.filter(
    (m) => m.tipo === tipo && m.moneda === moneda && m.sourceItemType !== 'distribucion',
  );

  const totals = new Map<string, number>();
  for (const m of filtered) {
    const cat = m.categoria;
    totals.set(cat, (totals.get(cat) ?? 0) + Math.abs(m.monto));
  }

  const total = Array.from(totals.values()).reduce((acc, v) => acc + v, 0);
  if (total === 0) return [];

  const sorted = Array.from(totals.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);

  const slices: DistribucionSlice[] = top.map(({ name, value }) => ({
    name,
    value,
    porcentaje: Math.round((value / total) * 100),
  }));

  if (rest.length > 0) {
    const othersValue = rest.reduce((acc, { value }) => acc + value, 0);
    slices.push({
      name: 'Otros',
      value: othersValue,
      porcentaje: Math.round((othersValue / total) * 100),
    });
  }

  return slices;
}
